import React, { useEffect, useState } from 'react';
import { pendingDrawsLine, PendingDrawEntries } from '../../../../utils/pendingDraws';
import { winnerDisplayName } from '../../../../utils/winnerName';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../../contexts/UserContext';
import { isProfileIncomplete } from '../../../../utils/profileCompleteness';
import { useNetwork } from '../../../../utils/network';
import { TwoFactorAuthModal } from '../../TwoFactorAuth/TwoFactorAuthModal';
import { ReferralModal } from '../../Home/components/ReferalModal';
import { UnclaimedPacksWidget } from '../components/UnclaimedPacksWidget/UnclaimedPacksWidget';
import { Faucet } from '../components/Faucet/Faucet';
import { GiveawayDashboard } from '../components/GiveawayDashboard/GiveawayDashboard';
import { DiscordInvite } from '../components/DiscordInvite';
import { MigrateBillingPrompt } from '../components/MigrateBillingPrompt/MigrateBillingPrompt';
import { LastWinner, PurchasePackagesType } from '../../../../types/types';
import { promoHeadline } from '../../../PromoPack/promoPack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { createPortal } from 'react-dom';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BoltIcon from '@mui/icons-material/Bolt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import TokenIcon from '@mui/icons-material/Token';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import './Dashboard.css';

type DashboardProps = {
  handlePurchaseSelection: (purchasePackage: PurchasePackagesType) => void;
};

const RAYDIUM_URL =
  'https://raydium.io/swap/?inputMint=sol&outputMint=Fe5c469ADZyvnZrB8gdDsYt5eLKodaVYhYJCdunVr1nA';

const Dashboard = ({ handlePurchaseSelection }: DashboardProps) => {
  const navigate = useNavigate();
  const { user, setUser, userCurrentSubscription, setUserCurrentSubscription } =
    useUser();
  const { get } = useNetwork();

  // Keep subscription status live: user.hasActiveSubscription is baked at LOGIN,
  // so it goes stale if you subscribe/cancel mid-session. Re-fetch the current
  // subscription on mount and fold it into isActive below — no re-login needed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await get('user/subscription', true);
        if (!cancelled) {
          setUserCurrentSubscription({
            package: result?.package ?? null,
            endDate: result?.endDate ?? null
          });
        }
      } catch (e) {
        /* keep whatever the login flag / context already has */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* While the shell holds the member in a gate — choose a username, complete
     your details, answer the giveaway-eligibility question (a member a partner
     such as Empire sent us can arrive owing all three) — the first-visit popups
     below stay closed: a later-opened MUI modal owns the focus trap, so one of
     these on top of a gate left the gate unable to take typing. They open once
     the gates are done; the state is untouched. */
  const gateOwed = isProfileIncomplete(user);

  const [isTwoFAModalOpen, setIsTwoFAModalOpen] = useState<boolean>(
    (!user.hasTwoFactorAuthEnabled && user.showTwoFactorAuth) ?? true
  );
  const [isReferralModalOpen, setIsReferralModalOpen] = useState<boolean>(
    (!isTwoFAModalOpen && user.showReferral) ?? true
  );

  const [lastWinner, setLastWinner] = useState<LastWinner | null>(null);
  const [loadingWinner, setLoadingWinner] = useState<boolean>(true);
  const [promotionPack, setPromotion] = useState<PurchasePackagesType | null>(
    null
  );
  const [promoEntries, setPromoEntries] = useState<number | null>(null);
  // Tickets in a promo draw closed but not yet spun (a postponed draw).
  const [promoPending, setPromoPending] = useState<PendingDrawEntries[]>([]);
  const [prizePool, setPrizePool] = useState<number | null>(null);
  const [giveawayStats, setGiveawayStats] = useState<{
    completedGiveaways: number;
    totalGivenAwayAud: number;
  } | null>(null);
  const [fabOpen, setFabOpen] = useState(false);
  const [packCount, setPackCount] = useState(0);
  const [packsModalOpen, setPacksModalOpen] = useState(false);
  const [kyc, setKyc] = useState<{
    status: 'not_started' | 'pending' | 'approved' | 'declined';
    // value = the pack's price in AUD, straight from the package row (never hardcoded here)
    reward: { offered: boolean; granted: boolean; pending: boolean; value?: number | null };
    /* the starter-pack LOCK (server lib/starterLock): a sign-up pack's cards cannot be listed
       or withdrawn and the account cannot play ranked until KYC or A$unlockAud spent */
    starterPack: { locked: boolean; unlockAud?: number; spentAud?: number };
  } | null>(null);
  const [faucetReady, setFaucetReady] = useState(false);
  // Bumped by the FAB's "Claim faucet" item to open the faucet captcha flow.
  const [faucetClaimSignal, setFaucetClaimSignal] = useState(0);
  // Whether this member's subscription still has to move off the old Stripe
  // account. MigrateBillingPrompt asks the server and reports back here, so the
  // FAB can list "Update payment details" without a second request.
  const [billingMoveNeeded, setBillingMoveNeeded] = useState(false);
  // Bumped by that FAB item to reopen the billing prompt.
  const [billingPromptSignal, setBillingPromptSignal] = useState(0);
  // Total actionable notifications for the FAB badge (billing + packs + faucet).
  const notifCount =
    packCount + (faucetReady ? 1 : 0) + (billingMoveNeeded ? 1 : 0);

  const handleTwoFAModalClose = (): void => {
    setUser({ ...user, showTwoFactorAuth: false });
    setIsTwoFAModalOpen(false);
    setIsReferralModalOpen(true);
  };

  const handleReferralModalClose = (): void => {
    setIsReferralModalOpen(false);
    setUser({ ...user, showReferral: false });
  };

  useEffect(() => {
    if (!isTwoFAModalOpen && user.showReferral) {
      setIsReferralModalOpen(true);
    }
  }, [isTwoFAModalOpen, user.showReferral]);

  useEffect(() => {
    const getUserBalances = async () => {
      try {
        const result = await get('/secure-tokens/balances', true);
        const balances = result?.balances;
        if (!balances) return;
        setUser((currentUser) => ({
          ...currentUser,
          tokenAmount: balances.tokenAmount ?? currentUser.tokenAmount,
          earnedTokens: balances.earnedTokens ?? currentUser.earnedTokens
        }));
      } catch (error: any) {
        console.log('Failed to fetch user balances:', error);
      }
    };

    const getUserGiveawayEntries = async () => {
      try {
        const result = await get('user/get-user-giveaway-entries', true);
        setUser((currentUser) => ({
          ...currentUser,
          entries: result.entries,
          stackingEntries: result.stackingEntries,
          pendingJackpot: result.pendingJackpot || []
        }));
      } catch (error: any) {
        console.log(error);
      }
    };

    const getLastGiveawayWinner = async () => {
      try {
        setLoadingWinner(true);
        const result = await get('/giveaway/last-winner', true);
        setLastWinner(result);
      } catch (error: any) {
        console.log('Failed to fetch last giveaway winner:', error);
        setLastWinner(null);
      } finally {
        setLoadingWinner(false);
      }
    };

    const getPromotion = async () => {
      try {
        const result = await get('/package/promotion', true);
        setPromotion(result);
      } catch (error: any) {
        console.log('Failed to fetch promotion', error);
      }
    };

    const getPromoEntries = async () => {
      try {
        const result = await get('/user/promo-entries', true);
        setPromoEntries(Number(result?.entries || 0));
        setPromoPending(result?.pending || []);
      } catch (error: any) {
        console.log('Failed to fetch promo entries', error);
      }
    };

    /* Drives the free-pack tile. /kyc/status reports both the member's verification state and
       whether a pack is on offer / already granted, so the tile can say the right thing at each
       stage instead of advertising a free pack to someone who has already claimed it.
       `configured: false` (or a failed call) leaves kyc null and the tile simply doesn't render
       — the offer is hidden rather than dangled at members who can't act on it. */
    const getKyc = async () => {
      try {
        const result = await get('/kyc/status', true);
        if (result?.configured === false) return;
        setKyc({
          status: result?.status || 'not_started',
          reward: {
            offered: !!result?.reward?.offered,
            granted: !!result?.reward?.granted,
            pending: !!result?.reward?.pending,
            value: result?.reward?.value ?? null
          },
          starterPack: {
            locked: !!result?.starterPack?.locked,
            unlockAud: result?.starterPack?.unlockAud,
            spentAud: result?.starterPack?.spentAud
          }
        });
      } catch (error: any) {
        console.log('Failed to fetch KYC status', error);
      }
    };

    const getPrizePool = async () => {
      try {
        // /tcg/prize-pool pays out per place in INC (`places[].rewardTokens`, the
        // same source the arena + leaderboard use). The pool is their sum.
        const pp = await get('/tcg/prize-pool', true);
        const places = Array.isArray(pp?.places) ? pp.places : [];
        const total = places
          .map((p: { rewardTokens?: string | number }) => Number(p?.rewardTokens))
          .filter((n: number) => Number.isFinite(n))
          .reduce((a: number, b: number) => a + b, 0);
        setPrizePool(total > 0 ? total : null);
      } catch (error: any) {
        setPrizePool(null);
      }
    };

    const getGiveawayStats = async () => {
      try {
        const result = await get('/giveaway/stats', true);
        setGiveawayStats({
          completedGiveaways: Number(result?.completedGiveaways || 0),
          totalGivenAwayAud: Number(result?.totalGivenAwayAud) || 0
        });
      } catch (error: any) {
        console.log('Failed to fetch giveaway stats:', error);
      }
    };

    getUserBalances();
    getUserGiveawayEntries();
    getLastGiveawayWinner();
    getPromotion();
    getPromoEntries();
    getPrizePool();
    getGiveawayStats();
    getKyc();
    // Fetch once on mount (get/setUser are stable for this screen's lifetime).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (path: string) => navigate(path);

  const formatTokenBalance = (tokenAmount: string | undefined): string => {
    if (!tokenAmount || tokenAmount === '0') return '0.00000';
    const numericAmount = parseFloat(tokenAmount);
    if (isNaN(numericAmount)) return '0.00000';
    return numericAmount.toFixed(5);
  };

  const formatCompactTokenBalance = (tokenAmount: string | undefined) => {
    const numericAmount = Number(tokenAmount || 0);
    if (!Number.isFinite(numericAmount)) return '0.00000';
    if (Math.abs(numericAmount) >= 1000000) {
      return new Intl.NumberFormat('en-AU', {
        notation: 'compact',
        maximumFractionDigits: 2
      }).format(numericAmount);
    }
    return formatTokenBalance(tokenAmount);
  };

  const walletInc = parseFloat(user.tokenAmount || '0');
  const earnedInc = parseFloat(user.earnedTokens || '0');
  const tokenBalance = formatCompactTokenBalance(
    (walletInc + earnedInc).toString()
  );

  const firstName = (user.firstName || '').trim() || 'Member';
  // A subscription with no end date, or an end date still in the future, is
  // active/renewing (mirrors the server's own check). This makes the tile go
  // live the moment the fetch above resolves, without waiting for a re-login.
  const liveSubActive =
    !!userCurrentSubscription?.package &&
    (userCurrentSubscription.endDate == null ||
      new Date(userCurrentSubscription.endDate).getTime() > Date.now());
  const isActive = !!user.hasActiveSubscription || liveSubActive;
  const today = new Date().toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });

  const winnerName = lastWinner
    ? winnerDisplayName(
        lastWinner.firstName,
        lastWinner.lastInitial,
        lastWinner.username
      )
    : '';

  // this week's headline, read as the public /promo page reads it ('' when
  // there is none — it resets with the promo draw)
  const promoHeadlineText = promoHeadline(promotionPack?.rewards);

  return (
    <div className="dash">
      {/* ---------- hero ---------- */}
      <header className="dash-hero">
        <div className="kick">Member Dashboard</div>
        <h1>
          Welcome back, <span className="cy">{firstName}</span>
        </h1>
        <div className="sub">
          {user.username && <span>@{user.username}</span>}
          {user.username && <span className="dot" />}
          <span className={`chip${isActive ? ' on' : ''}`}>
            <span className="pip" />
            {isActive ? 'Member active' : 'Free member'}
          </span>
          <span className="dot" />
          <span>{today}</span>
        </div>
      </header>

      {/* ---------- priority: the giveaway at a glance, right under the hero ---------- */}
      {/* row 1 — your tokens · the 10K timer · your entries */}
      <section className="dash-grid g3">
        {/* my INC — the token balance, kept at the very top */}
        <div className="dash-panel accent-cyan">
          <div className="dash-phead">
            <AccountBalanceWalletIcon className="ic" /> My INC
          </div>
          <div className="dash-pbody">
            <div className="dash-big cy" title={tokenBalance}>
              {tokenBalance}
            </div>
            <div className="dash-unit">INC tokens</div>
            <div className="dash-split">
              <div className="cell">
                <div className="n cy">
                  {formatCompactTokenBalance(walletInc.toString())}
                </div>
                <div className="l">Wallet</div>
              </div>
              <div className="cell">
                <div className="n">
                  {formatCompactTokenBalance(earnedInc.toString())}
                </div>
                <div className="l">Earned</div>
              </div>
            </div>
          </div>
          <div className="dash-prow">
            <button
              type="button"
              className="dash-btn solid"
              data-track="dashboard.view_token"
              onClick={() => go('token')}
            >
              View details
            </button>
            <a
              className="dash-btn ghost"
              href={RAYDIUM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-track="dashboard.buy_raydium"
            >
              Buy on Raydium
            </a>
          </div>
        </div>

        {/* 10K monthly giveaway timer */}
        <GiveawayDashboard />

        {/* my entries */}
        <div className="dash-panel accent-cyan">
          <div className="dash-phead gold">
            <ConfirmationNumberIcon className="ic" /> My entries
          </div>
          <div className="dash-pbody">
            <div className="dash-big gold">
              {(user.entries ?? 0).toLocaleString('en-AU')}
            </div>
            <div className="dash-unit">in the $10,000 giveaway</div>
            {pendingDrawsLine(user.pendingJackpot) && (
              <p className="dash-note">{pendingDrawsLine(user.pendingJackpot)}</p>
            )}
          </div>
          <div className="dash-prow">
            <button
              type="button"
              className="dash-btn gold"
              data-track="dashboard.entries_buy"
              onClick={() => go('giveaway')}
            >
              Get more entries
            </button>
          </div>
        </div>
      </section>

      {/* Free starter pack for verifying. Rendered only while this member still stands to gain
          something — either they can still earn a pack, or they have one sitting unopened.
          The `status !== 'approved'` half matters because grants are NOT retroactive: a member
          who verified before this launched will never receive one, and without that check the
          tile kept offering a pack to someone who can never get it. It also hides itself once a
          granted pack has actually been opened. */}
      {/* The starter-pack LOCK: the member has their free pack (granted at sign-up) but it is
          locked — say what is locked and the two ways out, with a button for each. */}
      {kyc?.starterPack.locked && (
        <section className="dash-grid">
          <div className="dash-panel kyc">
            <div className="dash-phead">
              <VerifiedUserIcon className="ic" /> Your starter pack is locked
            </div>
            <div className="dash-pbody">
              <p className="dash-note">
                Your free Relic Starter Pack is yours to play with in casual matches. Until you
                verify your identity or spend A${kyc.starterPack.unlockAud ?? 99} on INCRYPT, its cards
                can't be listed or withdrawn and ranked play is closed.
                {kyc.starterPack.spentAud != null && kyc.starterPack.spentAud > 0
                  ? ` You've spent A$${kyc.starterPack.spentAud.toFixed(2)} so far.`
                  : ''}
              </p>
            </div>
            <div className="dash-prow">
              <button type="button" className="dash-btn solid" data-track="dashboard.unlock_verify" onClick={() => go('kyc')}>
                Verify my identity
              </button>
              <button type="button" className="dash-btn" data-track="dashboard.unlock_buy" onClick={() => go('giveaway')}>
                Buy packs
              </button>
            </div>
          </div>
        </section>
      )}
      {kyc?.reward.offered &&
        (kyc.reward.pending || kyc.status !== 'approved') &&
        // a granted pack that has been opened is no longer an offer (the sign-up path grants before KYC)
        !(kyc.reward.granted && !kyc.reward.pending) && (
        <section className="dash-grid">
          <div className="dash-panel kyc">
            <div className="dash-phead">
              <VerifiedUserIcon className="ic" />{' '}
              {kyc.reward.pending ? 'Your free pack is ready' : 'Free Relic Starter Pack'}
            </div>
            <div className="dash-pbody">
              {kyc.reward.pending ? (
                <>
                  <div className="dash-big">1</div>
                  <div className="dash-unit">unopened Relic Starter Pack</div>
                  <p className="dash-note">
                    {kyc.status === 'approved'
                      ? 'All done — your pack is waiting to be opened.'
                      : `Your pack is waiting to be opened. Heads up: opening it locks its cards from trading and withdrawal, and closes ranked play, until you verify your identity or spend A$${kyc.starterPack.unlockAud ?? 99} on INCRYPT. Casual play stays open.`}
                  </p>
                </>
              ) : (
                <>
                  <div className="dash-big">
                    FREE
                    {/* the pack's real price, struck through — "free" lands harder with a
                        number beside it. Server-supplied, so it can't drift from the package. */}
                    {kyc.reward.value != null && (
                      <span className="dash-was">
                        ${Number.isInteger(kyc.reward.value)
                          ? kyc.reward.value
                          : kyc.reward.value.toFixed(2)}{' '}
                        AUD
                      </span>
                    )}
                  </div>
                  <div className="dash-unit">Relic Starter Pack</div>
                  {/* The tile leads with the offer and leaves the requirement to the claim page.
                      Deliberate sequencing, not concealment: /kyc is the consent point and still
                      states plainly that they submit an ID and a selfie, and that we never see
                      or store the documents. Nothing can be submitted before they read that, so
                      don't "helpfully" restore the mention here — but never remove it there. */}
                  <p className="dash-note">
                    {kyc.status === 'pending'
                      ? 'Your claim is being reviewed. Your pack lands here the moment it clears.'
                      : kyc.status === 'declined'
                        ? "Your last claim couldn't be approved — you can try again."
                        : 'A full 50-card starter deck, yours free.'}
                  </p>
                </>
              )}
            </div>
            <div className="dash-prow">
              <button
                type="button"
                className={`dash-btn ${kyc.reward.pending ? 'solid' : 'alert'}`}
                data-track="dashboard.claim_kyc_pack"
                onClick={() => (kyc.reward.pending ? setPacksModalOpen(true) : go('kyc'))}
              >
                {kyc.reward.pending
                  ? 'Open my pack'
                  : kyc.status === 'pending'
                    ? 'Check your claim'
                    : 'Claim your free pack'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* row 2 — packs · latest winner · giveaway totals */}
      <section className="dash-grid g3">
        {/* promotion OR one-off packs upsell */}
        {promotionPack !== null ? (
          <div className="dash-panel promo">
            <div className="dash-phead">
              <BoltIcon className="ic" /> Promo pack
            </div>
            <div className="promo-split">
              <div className="promo-main">
                <div className="dash-pbody">
                  <div className="pmsg">
                    Special <span className="hl">Promotion Pack</span> available
                    now
                  </div>
                  {/* This week's headline, when the admin wrote one — one
                      line, the full text in the tooltip. */}
                  {promoHeadlineText && (
                    <div className="phl" title={promoHeadlineText}>
                      {promoHeadlineText}
                    </div>
                  )}
                </div>
                <div className="dash-prow">
                  <button
                    type="button"
                    className="dash-btn gold"
                    data-track="dashboard.buy_promo"
                    onClick={() => handlePurchaseSelection(promotionPack)}
                  >
                    Buy now
                  </button>
                </div>
              </div>
              <div className="promo-entries">
                <div className="dash-big gold">{promoEntries ?? '—'}</div>
                <div className="dash-unit">Your promo entries</div>
              </div>
            </div>
            {/* Under the whole tile, full width: inside the entries column it
                would widen that column and squeeze the copy beside it. */}
            {pendingDrawsLine(promoPending) && (
              <p className="dash-note promo-pending">
                {pendingDrawsLine(promoPending)}
              </p>
            )}
          </div>
        ) : (
          <div className="dash-panel">
            <div className="dash-phead gold">
              <Inventory2Icon className="ic" /> Promo packs
            </div>
            <div className="dash-pbody">
              <div className="dash-big gold">30</div>
              <div className="dash-unit">digital collectibles + up to 30 entries</div>
              <p className="dash-note">Buy a one-off pack — no subscription needed.</p>
            </div>
            <div className="dash-prow">
              <button
                type="button"
                className="dash-btn gold"
                data-track="dashboard.buy_packs"
                onClick={() => go('giveaway')}
              >
                Buy packs
              </button>
            </div>
          </div>
        )}

        {/* latest giveaway winner */}
        <div className="dash-panel">
          <div className="dash-phead gold">
            <EmojiEventsIcon className="ic" /> Last winner
          </div>
          <div className="dash-center dash-winner">
            {loadingWinner ? (
              <div className="dash-state">Loading…</div>
            ) : lastWinner ? (
              <>
                <div className="nm">{winnerName}</div>
                {lastWinner.username && (
                  <div className="at">username: {lastWinner.username}</div>
                )}
                {lastWinner.discordUsername && (
                  <div className="dc">Discord: {lastWinner.discordUsername}</div>
                )}
              </>
            ) : (
              <div className="dash-state">No recent winners</div>
            )}
          </div>
        </div>

        {/* giveaway totals */}
        <div className="dash-panel">
          <div className="dash-phead gold">
            <EmojiEventsIcon className="ic" /> Giveaway totals
          </div>
          <div className="dash-totals">
            <div className="t">
              <div className="n gold">
                {giveawayStats
                  ? `A$${giveawayStats.totalGivenAwayAud.toLocaleString('en-AU', {
                      maximumFractionDigits: 0
                    })}`
                  : '—'}
              </div>
              <div className="l">Given away</div>
            </div>
            <div className="t">
              <div className="n cy">
                {giveawayStats
                  ? giveawayStats.completedGiveaways.toLocaleString('en-AU')
                  : '—'}
              </div>
              <div className="l">Completed draws</div>
            </div>
          </div>
        </div>
      </section>

      {/* row 3 — membership · battle · unwrap · faucet */}
      <section className="dash-grid g4">
        {/* membership */}
        <button
          type="button"
          className={`dash-kpi clk ${isActive ? 'k-green' : 'k-mute'}`}
          data-track="dashboard.kpi_membership"
          onClick={() => go('subscription')}
        >
          <span className="lab">
            <CardMembershipIcon className="ic" /> Subscription
          </span>
          <span className="val">{isActive ? 'Active' : 'Inactive'}</span>
          <span className="foot">
            {isActive ? 'manage plan' : 'subscribe to earn'}
          </span>
        </button>

        {/* duel arena */}
        <div className="dash-panel accent-cyan">
          <div className="dash-phead">
            <SportsEsportsIcon className="ic" /> Trading Card Game
          </div>
          <div className="dash-pbody">
            {prizePool !== null ? (
              <>
                <div className="dash-big gold">
                  {prizePool.toLocaleString('en-AU')}
                </div>
                <div className="dash-unit">INC in this week&apos;s prize pool</div>
              </>
            ) : (
              <>
                <div className="dash-big cy">Battle</div>
                <div className="dash-unit">for the weekly prize pool</div>
              </>
            )}
            <p className="dash-note">
              Win ranked matches to climb the weekly leaderboard and earn INC.
            </p>
          </div>
          <div className="dash-prow">
            <button
              type="button"
              className="dash-btn solid"
              data-track="dashboard.play_arena"
              onClick={() => go('play')}
            >
              Play now
            </button>
            <button
              type="button"
              className="dash-btn ghost"
              data-track="dashboard.view_leaderboard"
              onClick={() => go('Leaderboard')}
            >
              Leaderboard
            </button>
          </div>
        </div>

        {/* package unwrap (self-hides when none) */}
        <UnclaimedPacksWidget
          packsModalOpen={packsModalOpen}
          onPacksModalOpenChange={setPacksModalOpen}
          onCountChange={setPackCount}
        />

        {/* inc faucet */}
        <Faucet
          embedded
          onReadyChange={setFaucetReady}
          claimSignal={faucetClaimSignal}
        />
      </section>

      {/* ---------- community ---------- */}
      <div style={{ marginTop: 14 }}>
        <DiscordInvite />
      </div>

      {/* ---------- floating quick actions (bottom-right) ----------
          Portaled to <body>: the .arena-fade route wrapper keeps an
          entrance-animation transform, which makes it a containing block and
          would pin this position:fixed FAB to the scrolling page rather than the
          viewport. Rendering into <body> escapes that so it stays put on scroll. */}
      {createPortal(
        <>
          {fabOpen && (
            <div className="dash-fab-scrim" onClick={() => setFabOpen(false)} />
          )}
          <div className="dash-fab-wrap">
        {fabOpen && (
          <div className="dash-fab-menu" role="menu">
            {/* First: it is the only item here that keeps a subscription
                running, and it stays until they move (or it is done). */}
            {billingMoveNeeded && (
              <button
                type="button"
                className="dash-fab-item alert"
                data-track="dashboard.fab_billing"
                onClick={() => {
                  setFabOpen(false);
                  setBillingPromptSignal((s) => s + 1);
                }}
              >
                <span className="fic">
                  <CreditCardIcon />
                </span>
                <span className="ftx">
                  <span className="t">Update payment details</span>
                  <span className="d">We&apos;ve switched providers</span>
                </span>
              </button>
            )}
            {packCount > 0 && (
              <button
                type="button"
                className="dash-fab-item alert"
                data-track="dashboard.fab_unpack"
                onClick={() => {
                  setFabOpen(false);
                  setPacksModalOpen(true);
                }}
              >
                <span className="fic">
                  <Inventory2Icon />
                </span>
                <span className="ftx">
                  <span className="t">Unpack packs</span>
                  <span className="d">
                    {packCount} ready to open
                  </span>
                </span>
              </button>
            )}
            {faucetReady && (
              <button
                type="button"
                className="dash-fab-item alert"
                data-track="dashboard.fab_faucet"
                onClick={() => {
                  setFabOpen(false);
                  setFaucetClaimSignal((s) => s + 1);
                }}
              >
                <span className="fic">
                  <WaterDropIcon />
                </span>
                <span className="ftx">
                  <span className="t">Claim faucet</span>
                  <span className="d">Free INC ready</span>
                </span>
              </button>
            )}
            {(
              [
                { label: 'Subscribe', d: 'Monthly INC + assets', icon: <CardMembershipIcon />, to: 'subscription', tk: 'fab_subscribe' },
                { label: 'Buy packs', d: 'One-off collectibles', icon: <Inventory2Icon />, to: 'giveaway', tk: 'fab_packs' },
                { label: 'Play TCG', d: 'Duel for INC', icon: <SportsEsportsIcon />, to: 'play', tk: 'fab_play' },
                { label: 'Collectibles', d: 'Your cards', icon: <CollectionsBookmarkIcon />, to: 'digital-collectibles', tk: 'fab_collection' },
                { label: 'INC token', d: 'Balance & wallet', icon: <TokenIcon />, to: 'token', tk: 'fab_token' },
                { label: 'Leaderboard', d: 'Weekly ranks', icon: <LeaderboardIcon />, to: 'Leaderboard', tk: 'fab_leaderboard' }
              ] as { label: string; d: string; icon: React.ReactNode; to: string; tk: string }[]
            ).map((a) => (
              <button
                key={a.label}
                type="button"
                className="dash-fab-item"
                data-track={`dashboard.${a.tk}`}
                onClick={() => {
                  setFabOpen(false);
                  go(a.to);
                }}
              >
                <span className="fic">{a.icon}</span>
                <span className="ftx">
                  <span className="t">{a.label}</span>
                  <span className="d">{a.d}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          className={`dash-fab${fabOpen ? ' open' : ''}`}
          aria-label="Quick actions"
          aria-expanded={fabOpen}
          onClick={() => setFabOpen((o) => !o)}
        >
          {fabOpen ? <CloseRoundedIcon /> : <AppsRoundedIcon />}
          {!fabOpen && notifCount > 0 && (
            <span className="dash-fab-badge">{notifCount}</span>
          )}
        </button>
      </div>
        </>,
        document.body
      )}

      {isTwoFAModalOpen && !gateOwed && (
        <TwoFactorAuthModal
          isTwoFAModalOpen={isTwoFAModalOpen}
          handleTwoFAModalClose={handleTwoFAModalClose}
        />
      )}

      {isReferralModalOpen && !gateOwed && (
        <ReferralModal
          isOpen={isReferralModalOpen}
          handleClose={handleReferralModalClose}
        />
      )}

      {/* Third in the modal queue, behind 2FA and the referral prompt — three at
          once would be three things to dismiss before reaching the page. It asks
          only when the server says the migration will actually succeed, and it
          renders nothing at all for everybody else. Asked again at every sign-in,
          and listed in the quick-actions menu above (counted in its badge) in
          between, until they move their billing across. */}
      <MigrateBillingPrompt
        enabled={!isTwoFAModalOpen && !isReferralModalOpen}
        openSignal={billingPromptSignal}
        onNeedsMigrationChange={setBillingMoveNeeded}
      />
    </div>
  );
};

export default Dashboard;
